<script lang="ts">
	import { page } from '$app/state';
	import CartHeader from '$lib/components/cart/CartHeader.svelte';
	import type { Market } from '$lib/types/market';
	import type { User } from '$lib/types/user';
	import { openSentryFeedback, showFeedbackError } from '$lib/utils/feedback';

	let {
		market,
		user,
		loggedIn = false,
		showHelpButton = true,
		helpContent,
		onTourClick,
	} = $props<{
		market?: Market | null;
		user?: User | null;
		loggedIn?: boolean;
		showHelpButton?: boolean;
		onHelpClick?: () => void;
		helpContent?: string;
		onTourClick?: () => void;
	}>();

	let showHelpBox = $state(false);
	let showMobileMenu = $state(false);

	function isCurrentPath(path: string): boolean {
		return page.url.pathname === path;
	}

	function startsWithPath(path: string): boolean {
		return page.url.pathname.startsWith(path);
	}

	// Function to trigger Sentry feedback with improved error handling
	async function openFeedback() {
		const success = await openSentryFeedback(user, loggedIn);
		if (!success) {
			showFeedbackError();
		}
	}
</script>

<nav class="modern-navbar">
	<div class="navbar-container">
		<!-- Mobile menu button -->
		<span class="mobile-menu-button-text">{showMobileMenu ? 'Close' : 'Menu'}</span>
		<button
			type="button"
			class="mobile-menu-button"
			onclick={() => (showMobileMenu = !showMobileMenu)}
			aria-label="Toggle navigation menu"
			aria-expanded={showMobileMenu}
		>
			<svg
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2"
				class="hamburger-icon"
				class:hidden={showMobileMenu}
			>
				<line x1="3" y1="6" x2="21" y2="6"></line>
				<line x1="3" y1="12" x2="21" y2="12"></line>
				<line x1="3" y1="18" x2="21" y2="18"></line>
			</svg>
			<svg
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2"
				class="close-icon"
				class:hidden={!showMobileMenu}
			>
				<line x1="18" y1="6" x2="6" y2="18"></line>
				<line x1="6" y1="6" x2="18" y2="18"></line>
			</svg>
		</button>

		<!-- Desktop navigation -->
		<ul class="navbar-nav desktop-nav">
			<li class="nav-item">
				<a href="/welcome" class="nav-link" class:active={isCurrentPath('/welcome')}>About</a>
			</li>
			{#if market}
				<li class="nav-item">
					<a href="/market" class="nav-link" class:active={isCurrentPath('/market')}>Shop Local</a>
				</li>
			{:else}
				<li class="nav-item">
					<a href="/markets/list" class="nav-link" class:active={isCurrentPath('/markets/list')}
						>Our Markets</a
					>
				</li>
			{/if}
			{#if !market || !market.recipesDisabled}
				<li class="nav-item">
					<a href="/recipes" class="nav-link" class:active={startsWithPath('/recipes')}>Recipes</a>
				</li>
			{/if}
			<li class="nav-item">
				<a href="/weblog" class="nav-link" class:active={startsWithPath('/weblog')}>Weblog</a>
			</li>
			<li class="nav-item">
				<a href="/faq" class="nav-link" class:active={isCurrentPath('/faq')}>Q's and A's</a>
			</li>
			{#if market}
				<li class="nav-item">
					<a href="/growers" class="nav-link" class:active={startsWithPath('/growers')}
						>Our Growers</a
					>
				</li>
			{/if}
			{#if user && loggedIn && market}
				<CartHeader />
			{/if}
			{#if !user && market}
				<li class="nav-item">
					<a href="/signin?from={page.url.pathname}" class="nav-link">Sign In</a>
				</li>
			{:else if user && loggedIn}
				<li class="nav-item">
					<a href="/admin" class="nav-link" class:active={isCurrentPath('/admin')}>Your Account</a>
				</li>
				<li class="nav-item">
					<a
						href="/user/signout"
						class="nav-link"
						aria-label="Sign out"
						onclick={(e) => {
							e.preventDefault();
							if (confirm('Are you sure you want to sign out?')) {
								window.location.href = '/user/signout';
							}
						}}
					>
						Sign Out
					</a>
				</li>
			{/if}
			{#if showHelpButton}
				<li class="nav-item help-item">
					<button
						type="button"
						class="help-button"
						onclick={() => {
							showHelpBox = !showHelpBox;
						}}
						aria-label="Display help for this page"
						title="Display help for this page"
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
						>
							<circle cx="12" cy="12" r="10"></circle>
							<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
							<line x1="12" y1="17" x2="12.01" y2="17"></line>
						</svg>
					</button>
				</li>
			{/if}
			<!-- Feedback button -->
			<li class="nav-item">
				<button
					type="button"
					class="help-button feedback-button"
					onclick={openFeedback}
					aria-label="Send feedback"
					title="Send feedback to LocallyGrown.net"
				>
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
					>
						<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
					</svg>
				</button>
			</li>
		</ul>

		<!-- Mobile navigation menu -->
		{#if showMobileMenu}
			<div class="mobile-nav-menu">
				<ul class="mobile-nav-list">
					<li class="mobile-nav-item">
						<a
							href="/welcome"
							class="mobile-nav-link"
							class:active={isCurrentPath('/welcome')}
							onclick={() => (showMobileMenu = false)}
						>
							About
						</a>
					</li>
					{#if market}
						<li class="mobile-nav-item">
							<a
								href="/market"
								class="mobile-nav-link"
								class:active={isCurrentPath('/market')}
								onclick={() => (showMobileMenu = false)}
							>
								Shop Local
							</a>
						</li>
					{:else}
						<li class="mobile-nav-item">
							<a
								href="/markets/list"
								class="mobile-nav-link"
								class:active={isCurrentPath('/markets/list')}
								onclick={() => (showMobileMenu = false)}
							>
								Our Markets
							</a>
						</li>
					{/if}
					{#if !market || !market.recipesDisabled}
						<li class="mobile-nav-item">
							<a
								href="/recipes"
								class="mobile-nav-link"
								class:active={isCurrentPath('/recipes')}
								onclick={() => (showMobileMenu = false)}
							>
								Recipes
							</a>
						</li>
					{/if}
					<li class="mobile-nav-item">
						<a
							href="/weblog"
							class="mobile-nav-link"
							class:active={isCurrentPath('/weblog')}
							onclick={() => (showMobileMenu = false)}
						>
							Weblog
						</a>
					</li>
					<li class="mobile-nav-item">
						<a
							href="/faq"
							class="mobile-nav-link"
							class:active={isCurrentPath('/faq')}
							onclick={() => (showMobileMenu = false)}
						>
							Q's and A's
						</a>
					</li>
					{#if market}
						<li class="mobile-nav-item">
							<a
								href="/growers"
								class="mobile-nav-link"
								class:active={startsWithPath('/growers')}
								onclick={() => (showMobileMenu = false)}
							>
								Our Growers
							</a>
						</li>
					{/if}
					{#if user && loggedIn && market}
						<li class="mobile-nav-item">
							<a
								href="/cart/review"
								class="mobile-nav-link cart-link"
								onclick={() => (showMobileMenu = false)}
							>
								Your Basket
							</a>
						</li>
					{/if}
					{#if !user && market}
						<li class="mobile-nav-item">
							<a
								href="/signin?from={page.url.pathname}"
								class="mobile-nav-link"
								onclick={() => (showMobileMenu = false)}
							>
								Sign In
							</a>
						</li>
					{:else if user && loggedIn}
						<li class="mobile-nav-item">
							<a
								href="/admin"
								class="mobile-nav-link"
								class:active={isCurrentPath('/admin')}
								onclick={() => (showMobileMenu = false)}
							>
								Your Account
							</a>
						</li>
						<li class="mobile-nav-item">
							<a
								href="/user/signout"
								class="mobile-nav-link"
								onclick={(e) => {
									e.preventDefault();
									showMobileMenu = false;
									if (confirm('Are you sure you want to sign out?')) {
										window.location.href = '/user/signout';
									}
								}}
							>
								Sign Out
							</a>
						</li>
					{/if}
					{#if showHelpButton}
						<li class="mobile-nav-item">
							<button
								type="button"
								class="mobile-help-button"
								onclick={() => {
									showMobileMenu = false;
									showHelpBox = true;
								}}
							>
								Help
							</button>
						</li>
					{/if}
					<!-- Mobile Feedback button -->
					<li class="mobile-nav-item">
						<button
							type="button"
							class="mobile-help-button"
							onclick={() => {
								showMobileMenu = false;
								openFeedback();
							}}
						>
							Send Feedback
						</button>
					</li>
				</ul>
			</div>
		{/if}
	</div>
</nav>

<!-- Spacer for fixed mobile navbar -->
<div class="navbar-spacer"></div>

{#if showHelpBox}
	<div
		class="help-overlay"
		onclick={() => (showHelpBox = false)}
		onkeydown={(e) => e.key === 'Escape' && (showHelpBox = false)}
		role="presentation"
	>
		<div
			class="help-box"
			onclick={(e) => e.stopPropagation()}
			onkeydown={(e) => e.key === 'Escape' && (showHelpBox = false)}
			role="dialog"
			aria-labelledby="help-title"
			aria-modal="true"
			tabindex="-1"
		>
			<div class="help-header">
				<h3 id="help-title">Page Help</h3>
				<button
					type="button"
					class="close-button"
					onclick={() => (showHelpBox = false)}
					aria-label="Close help"
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
					>
						<line x1="18" y1="6" x2="6" y2="18"></line>
						<line x1="6" y1="6" x2="18" y2="18"></line>
					</svg>
				</button>
			</div>
			<div class="help-content">
				{#if helpContent}
					<p>{helpContent}</p>
				{:else}
					<p>Help information for this page would appear here.</p>
					<p>This is a placeholder help box that can be customized for each page.</p>
				{/if}
				{#if onTourClick}
					<div class="help-actions">
						<button
							type="button"
							class="tour-button"
							onclick={() => {
								showHelpBox = false;
								onTourClick();
							}}
						>
							Take a Tour
						</button>
					</div>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	.modern-navbar {
		background: var(--lg-bg-secondary);
		position: sticky;
		top: 0;
		z-index: 100;
		/* Add shadow when scrolled on mobile */
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	}

	/* Spacer is hidden by default (desktop) */
	.navbar-spacer {
		display: none;
	}

	.navbar-container {
		max-width: var(--lg-max-width-xl);
		margin: 0 auto;
	}

	.navbar-nav {
		display: flex;
		flex-wrap: wrap;
		list-style: none;
		margin: 0;
		margin-bottom: 0 !important;
		padding: 0;
		align-items: center;
		gap: var(--lg-space-2);
		list-style-type: none;
	}

	.nav-link {
		display: block;
		padding: var(--lg-space-3) var(--lg-space-4);
		color: var(--lg-text-primary);
		text-decoration: none;
		font-weight: var(--lg-font-medium);
		transition: all var(--lg-transition-base);
		border-radius: var(--lg-radius-md);
		white-space: nowrap;
	}

	.nav-link:hover {
		background: var(--lg-bg-hover);
		color: var(--lg-primary);
	}

	.nav-link.active {
		background: var(--lg-bg-secondary);
		color: var(--lg-primary);
		font-weight: bold;
	}

	.help-item {
		margin-left: auto;
	}

	.help-button {
		background: none;
		border: none;
		padding: var(--lg-space-2);
		color: var(--lg-text-muted);
		cursor: pointer;
		border-radius: var(--lg-radius-md);
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all var(--lg-transition-base);
	}

	.help-button:hover {
		background: var(--lg-bg-hover);
		color: var(--lg-text-primary);
	}

	.help-button:focus {
		outline: 2px solid var(--lg-primary);
		outline-offset: var(--lg-space-0-5);
	}

	.help-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: var(--lg-overlay-bg);
		z-index: 1000;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--lg-space-4);
	}

	.help-box {
		background: var(--lg-bg-secondary);
		border-radius: var(--lg-radius-lg);
		box-shadow: var(--lg-shadow-xl);
		max-width: var(--lg-max-width-md);
		width: 100%;
		max-height: var(--lg-vh-80);
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.help-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: var(--lg-space-6);
		border-bottom: 1px solid var(--lg-border-light);
	}

	.help-header h3 {
		margin: 0;
		color: var(--lg-text-primary);
		font-size: var(--lg-text-lg);
		font-weight: var(--lg-font-semibold);
	}

	.close-button {
		background: none;
		border: none;
		padding: var(--lg-space-1);
		color: var(--lg-text-muted);
		cursor: pointer;
		border-radius: var(--lg-radius-sm);
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all var(--lg-transition-base);
	}

	.close-button:hover,
	.close-button:focus,
	.close-button:active {
		background: var(--lg-bg-hover);
		color: var(--lg-text-primary);
	}

	.help-content {
		padding: var(--lg-space-6);
		overflow-y: auto;
	}

	.help-content p {
		margin: 0 0 var(--lg-space-4) 0;
		color: var(--lg-text-secondary);
		line-height: var(--lg-leading-relaxed);
	}

	.help-content p:last-child {
		margin-bottom: 0;
	}
	.help-actions {
		margin-top: var(--lg-space-6);
		display: flex;
		justify-content: center;
	}
	.tour-button {
		background: var(--lg-primary);
		color: var(--lg-text-white);
		border: none;
		padding: var(--lg-space-3) var(--lg-space-6);
		border-radius: var(--lg-radius-md);
		font-size: var(--lg-text-base);
		font-weight: var(--lg-font-semibold);
		cursor: pointer;
		transition: all var(--lg-transition-base);
	}
	.tour-button:hover {
		background: var(--lg-primary-hover);
		transform: translateY(-1px);
		box-shadow: var(--lg-shadow-md);
	}
	.tour-button:active {
		transform: translateY(0);
	}

	/* Mobile menu button - hidden on desktop */
	.mobile-menu-button {
		display: none;
		background: none;
		border: none;
		padding: var(--lg-space-2);
		color: var(--lg-text-primary);
		cursor: pointer;
		border-radius: var(--lg-radius-md);
		transition: all var(--lg-transition-base);
		margin-right: var(--lg-space-3);
	}

	.mobile-menu-button:hover {
		background: var(--lg-bg-hover);
	}

	.mobile-menu-button:focus {
		outline: 2px solid var(--lg-primary);
		outline-offset: var(--lg-space-0-5);
	}

	.mobile-menu-button-text {
		display: none;
		font-size: var(--lg-text-sm);
		font-weight: var(--lg-font-medium);
		color: var(--lg-text-primary);
		transition: all var(--lg-transition-base);
		margin-right: var(--lg-space-1);
	}

	.hidden {
		display: none;
	}

	/* Mobile navigation menu */
	.mobile-nav-menu {
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		background: var(--lg-bg-secondary);
		border-top: 1px solid var(--lg-border-light);
		box-shadow: var(--lg-shadow-md);
		z-index: 50;
	}

	.mobile-nav-list {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	.mobile-nav-item {
		border-bottom: 1px solid var(--lg-border-light);
		/* Remove any default padding/margin */
		margin: 0;
		padding: 0;
	}

	.mobile-nav-item:last-child {
		border-bottom: none;
	}

	.mobile-nav-link {
		display: block;
		padding: var(--lg-space-4) var(--lg-space-6);
		color: var(--lg-text-primary);
		text-decoration: none;
		font-weight: var(--lg-font-medium);
		transition: all var(--lg-transition-base);
		/* Make the entire area clickable */
		width: 100%;
		/* Minimum touch target height for mobile */
		min-height: 48px;
		display: flex;
		align-items: center;
	}

	.mobile-nav-link:hover,
	.mobile-nav-link:focus,
	.mobile-nav-link:active {
		background: var(--lg-bg-hover);
		color: var(--lg-primary);
	}

	.mobile-nav-link.active {
		background: var(--lg-bg-secondary);
		color: var(--lg-primary);
		font-weight: var(--lg-font-semibold);
	}

	.mobile-nav-link.cart-link {
		color: var(--lg-primary);
		font-weight: var(--lg-font-semibold);
	}

	.mobile-help-button {
		width: 100%;
		padding: var(--lg-space-4) var(--lg-space-6);
		background: none;
		border: none;
		color: var(--lg-text-primary);
		text-align: left;
		font-weight: var(--lg-font-medium);
		cursor: pointer;
		transition: all var(--lg-transition-base);
		/* Minimum touch target height for mobile */
		min-height: 48px;
		display: flex;
		align-items: center;
	}

	.mobile-help-button:hover,
	.mobile-help-button:focus,
	.mobile-help-button:active {
		background: var(--lg-bg-hover);
		color: var(--lg-primary);
	}

	/* Mobile responsive */
	@media (max-width: 768px) {
		.modern-navbar {
			/* Ensure navbar stays sticky on mobile */
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			z-index: 1000;
			/* Better shadow for mobile */
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		}

		/* Show spacer on mobile to push content down */
		.navbar-spacer {
			display: block;
			height: 52px; /* Same height as navbar */
		}

		.mobile-menu-button {
			display: block;
		}

		.mobile-menu-button-text {
			display: block;
		}

		.desktop-nav {
			display: none;
		}

		.navbar-container {
			position: relative;
			display: flex;
			align-items: center;
			justify-content: flex-end;
			min-height: 52px; /* Consistent navbar height */
		}

		/* Mobile menu should also be fixed */
		.mobile-nav-menu {
			position: fixed;
			top: 52px; /* Height of navbar */
			left: 0;
			right: 0;
			max-height: calc(100vh - 52px);
			overflow-y: auto;
			-webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
		}

		.help-box {
			margin: var(--lg-space-4);
			max-height: calc(100vh - var(--lg-space-8));
		}

		.help-header,
		.help-content {
			padding: var(--lg-space-4);
		}
	}

	/* Desktop styles */
	@media (min-width: 769px) {
		.mobile-nav-menu {
			display: none;
		}
	}
</style>
